import { type FC } from "react";
import { Check, X } from "lucide-react";

import { getInitials } from "@/shared/utils/formatters";
import { Input } from "@/shared/components/ui/input";
import { PeopleSelector } from "@/shared/components/PeopleSelector";
import type { ItemBeneficiary } from "@/shared/hooks/useItemBeneficiaries";
import type { PersonContact } from "@/shared/types/estateContact";

interface BeneficiaryEditorProps {
  beneficiaries: ItemBeneficiary[];
  contacts?: PersonContact[];
  localShares: Record<number, string>;
  onLocalShareChange: (id: number, value: string) => void;
  onShareBlur: (id: number) => void;
  onAdd: (params: { estate_item_id: number; contact_id: number }) => void;
  onRemove: (params: { id: number; estate_item_id: number }) => void;
  itemId: number;
}

export const BeneficiaryEditor: FC<BeneficiaryEditorProps> = ({
  beneficiaries,
  contacts,
  localShares,
  onLocalShareChange,
  onShareBlur,
  onAdd,
  onRemove,
  itemId,
}) => {
  const rawTotal = beneficiaries.reduce((sum, b) => sum + (b.share_percentage ?? 0), 0);
  const total = Math.round(rawTotal * 100) / 100;

  return (
    <>
      <div>
        <p className="mb-2 text-sm font-medium">Beneficiaries</p>
        {beneficiaries.length === 0 ? (
          <p className="text-sm text-tertiary">No beneficiaries added yet</p>
        ) : (
          <div className="flex flex-col divide-y divide-primary">
            {beneficiaries.map((b) => (
              <div key={b.id} className="flex items-center gap-3 py-4 first:pt-0 last:pb-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand text-2xs font-medium text-white">
                  {getInitials({ fullName: b.contact_name })}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{b.contact_name}</p>
                  {b.relationship && (
                    <p className="text-2xs capitalize text-secondary">{b.relationship}</p>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    value={
                      localShares[b.id] ??
                      (b.share_percentage != null ? String(b.share_percentage) : "")
                    }
                    onChange={(e) => onLocalShareChange(b.id, e.target.value)}
                    onBlur={() => onShareBlur(b.id)}
                    className="h-8 w-20 text-center text-sm"
                    placeholder="0"
                  />
                  <span className="text-sm font-medium">%</span>
                </div>
                <button
                  onClick={() => onRemove({ id: b.id, estate_item_id: itemId })}
                  className="text-tertiary hover:text-primary"
                  aria-label={`Remove ${b.contact_name}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <PeopleSelector
        mode="single"
        value={null}
        onChange={(contactId) =>
          contactId && onAdd({ estate_item_id: itemId, contact_id: contactId })
        }
        contacts={contacts}
        filter={(contact) => !beneficiaries.some((b) => b.contact_id === contact.id)}
        placeholder="Add a beneficiary"
      />
      <div className="flex items-center justify-end">
        {beneficiaries.length > 0 &&
          (() => {
            if (total === 100) {
              return (
                <p className="flex items-center gap-1 text-2xs font-medium text-tertiary">
                  <Check className="h-3.5 w-3.5" /> Shares total 100%
                </p>
              );
            }
            if (total > 100) {
              return (
                <p className="flex items-center gap-1 text-2xs font-medium text-error">
                  {total}% assigned &middot; {Math.round((total - 100) * 100) / 100}% over
                </p>
              );
            }
            return (
              <p className="flex items-center gap-1 text-2xs font-medium text-error">
                {total}% assigned &middot; {Math.round((100 - total) * 100) / 100}% remaining
              </p>
            );
          })()}
      </div>
    </>
  );
};
